// A hint's target, as a rectangle on screen.
//
// **On the renderer's frame, never a private `requestAnimationFrame`.** Two
// rAF callbacks run in registration order, so an overlay polling its own frame
// projects against either side of the renderer's update depending on the
// frame, and that *inconsistency* is what reads as shaking. `PieceOverlays`
// and `BoardLabels` both anchor this way; the trap is written up in CLAUDE.md
// and this is the third customer for it.
//
// A control's rect is the other half and it does **not** move with the camera,
// so the renderer's loop is the wrong clock for it on its own — that loop is
// on-demand and draws nothing while the board is still. So a resize is
// listened for as well, and every target is re-read when the hint changes.

import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

import type { BoardCanvasHandle } from "./BoardCanvas";
import { inside, type Rect, sameRect } from "./hintPlacement";
import type { HintTarget } from "./hintTargets";

/** A square's ring is never smaller than this, however far the camera is out. */
const MIN_RADIUS = 14;

/**
 * The rectangle to ring, or null when there is nothing to point at.
 *
 * Null for a **square that is not on the board's own area** — behind the
 * panel, under the header, off the edge. The card falls back to being parked
 * there, which is honest: a beak pointing off the screen claims a position it
 * does not have. A *control* is exempt, since a control is on the panel by
 * definition and ringing it there is the whole point.
 */
export function useHintAnchor(
  target: HintTarget | null,
  board: RefObject<BoardCanvasHandle | null>,
  bounds: Rect
): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null);

  // Read through refs, so the frame subscription is not torn down and rebuilt
  // every time the camera nudges the bounds — `onFrame` returns an
  // unsubscriber and re-running the effect per frame would churn the set.
  const latest = useRef({ target, bounds });
  latest.current = { target, bounds };

  const measure = useCallback(() => {
    const { target: at, bounds: area } = latest.current;
    let next: Rect | null = null;

    if (at?.kind === "control") {
      const element = document.querySelector(at.selector);
      const box = element?.getBoundingClientRect();
      if (box && (box.width > 0 || box.height > 0)) {
        next = {
          x: Math.round(box.x),
          y: Math.round(box.y),
          width: Math.round(box.width),
          height: Math.round(box.height)
        };
      }
    } else if (at?.kind === "square") {
      const centre = board.current?.projectCentre(at.square);
      const corner = board.current?.projectSquare(at.square);
      if (centre && inside(area, centre.x, centre.y)) {
        // **The size comes from the projection, not from a constant.** The
        // corner and the centre are the same square at whatever zoom the
        // player is at, so the gap between them is half of one — a fixed
        // radius would swallow a piece pulled right out and rattle round one
        // up close.
        const radius = corner
          ? Math.max(MIN_RADIUS, Math.hypot(corner.x - centre.x, corner.y - centre.y))
          : MIN_RADIUS;
        next = {
          x: Math.round(centre.x - radius),
          y: Math.round(centre.y - radius),
          width: Math.round(radius * 2),
          height: Math.round(radius * 2)
        };
      }
    }

    setRect((was) => (sameRect(was, next) ? was : next));
  }, [board]);

  useEffect(() => board.current?.onFrame(measure), [board, measure]);

  // The renderer only calls back on frames it drew, and a hint arriving on a
  // settled board draws nothing at all — so measure directly as well, on every
  // change that can move a target without moving the camera.
  const key =
    target === null
      ? "none"
      : target.kind === "square"
        ? `sq:${target.square}`
        : `ui:${target.selector}`;
  useEffect(() => {
    measure();
  }, [measure, key, bounds.x, bounds.y, bounds.width, bounds.height]);

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  return rect;
}
